import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, TriangleAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { TextField } from '../../../components/ui/Field';
import { ErrorState, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { contrastWithWhite } from '../../auth/branding';
import { tenantApi, tenantKeys, uploadImage, type Branding } from '../api';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

interface FormProps {
  submitLabel?: string;
  onSaved?: () => void;
}

/** Branding editor with live preview and a contrast warning (docs/design/15 §3.1 step 2). */
export function BrandingForm({ submitLabel = 'Save branding', onSaved }: FormProps) {
  const branding = useQuery({ queryKey: tenantKeys.branding, queryFn: tenantApi.branding });
  if (branding.isPending) return <TableSkeleton rows={4} />;
  if (branding.isError) return <ErrorState error={branding.error} onRetry={() => void branding.refetch()} />;
  // Remount the editor when the server copy changes, so its local draft starts from fresh data.
  return (
    <BrandingEditor
      key={branding.data.version}
      initial={branding.data}
      submitLabel={submitLabel}
      {...(onSaved ? { onSaved } : {})}
    />
  );
}

function BrandingEditor({
  initial,
  submitLabel,
  onSaved,
}: FormProps & { initial: Branding; submitLabel: string }) {
  const toast = useToast();
  const { reload } = useAuth();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [color, setColor] = useState(initial.primaryColor);
  const [sender, setSender] = useState(initial.emailSenderName ?? '');
  const [logo, setLogo] = useState<{ id: string; url: string | null } | null>(initial.logo);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: () =>
      tenantApi.updateBranding({
        displayName: displayName || null,
        primaryColor: color.toUpperCase(),
        emailSenderName: sender || null,
        logoId: logo?.id ?? null,
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(tenantKeys.branding, data);
      await reload();
      toast.success('Branding saved');
      onSaved?.();
    },
    onError: (err) => toast.error(err),
  });

  async function onFile(file: File | undefined) {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) return toast.error(new Error('Use a PNG, JPEG or WebP image.'));
    if (file.size > MAX_LOGO_BYTES) return toast.error(new Error('The logo must be 2 MB or smaller.'));
    setUploading(true);
    try {
      setLogo(await uploadImage(file, 'tenant_logo'));
      toast.success('Logo uploaded. Save to apply it.');
    } catch (err) {
      toast.error(err, 'The logo could not be uploaded.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const validColor = /^#[0-9A-Fa-f]{6}$/.test(color);
  const lowContrast = validColor && contrastWithWhite(color) < 4.5;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_18rem]">
      <div className="space-y-4">
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={150}
        />
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <TextField
              label="Brand colour"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              maxLength={7}
              error={validColor ? undefined : 'Use a hex colour like #2563EB'}
            />
          </div>
          <input
            aria-label="Pick brand colour"
            type="color"
            value={validColor ? color : '#2563EB'}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-12 cursor-pointer rounded-md border border-line bg-surface"
          />
        </div>
        {lowContrast ? (
          <p className="flex items-center gap-2 text-sm text-warning">
            <TriangleAlert aria-hidden className="size-4" /> White text is hard to read on this colour; the
            default blue will be used for buttons.
          </p>
        ) : null}
        <TextField
          label="Email sender name"
          hint="Shown as the sender of emails to your staff"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          maxLength={100}
        />
        <div>
          <p className="mb-1.5 text-sm font-medium text-fg">Logo</p>
          <div className="flex items-center gap-3">
            {logo?.url ? (
              <img
                src={logo.url}
                alt="Current logo"
                className="size-14 rounded-md border border-line object-contain"
              />
            ) : null}
            <input
              ref={fileInput}
              type="file"
              accept={LOGO_TYPES.join(',')}
              className="sr-only"
              id="logo-file"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            <Button
              variant="secondary"
              loading={uploading}
              leadingIcon={<ImageUp aria-hidden className="size-4" />}
              onClick={() => fileInput.current?.click()}
            >
              {logo ? 'Replace logo' : 'Upload logo'}
            </Button>
            {logo ? (
              <Button variant="ghost" onClick={() => setLogo(null)}>
                Remove
              </Button>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-fg-subtle">
            PNG, JPEG or WebP, up to 2 MB. Square images work best.
          </p>
        </div>
        <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!validColor || uploading}>
          {submitLabel}
        </Button>
      </div>
      <aside aria-label="Preview" className="rounded-lg border border-line bg-surface-muted p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-fg-subtle">Preview</p>
        <div className="flex items-center gap-2">
          {logo?.url ? (
            <img src={logo.url} alt="" className="size-8 rounded-md object-contain" />
          ) : (
            <span
              className="flex size-8 items-center justify-center rounded-md font-bold text-white"
              style={{ background: validColor && !lowContrast ? color : '#2563EB' }}
            >
              {(displayName || 'A').slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="font-semibold text-fg">{displayName || 'Your business'}</span>
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="mt-4 rounded-md px-4 py-2 text-sm font-medium text-white"
          style={{ background: validColor && !lowContrast ? color : '#2563EB' }}
        >
          Primary button
        </button>
      </aside>
    </div>
  );
}
